import NiceModal from '@ebay/nice-modal-react';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { Checkbox, Divider, List, MainTitle, Svg, StickyBottomWrapper, BoxButton, AdditionalInfo } from '@repo/ui';
import { useAppSignInContext, useAuthContext } from '@repo/user-session';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CI_REQUEST_TYPES } from '../constants';
import { useCiHistoryController } from '../hooks/use-ci-history-controller';
import { usePrepareCiAuthData } from '../hooks/use-prepare-ci-auth-data';
import { termsApprovalStateAtom } from '../store';
import { CiRequestTypes, Term } from '../types';

import { CIPrepareDataForm } from './CIPrepareDataForm';
import { TermDrawer } from './TermDrawer';

export const CICollectionRequest = () => {
  const { signRequiredStep } = useAppSignInContext();
  const { authService } = useAuthContext();
  const [termsContent, setTermsContent] = useState<Term[]>([]);
  const [termsApprovalState, setTermsApprovalState] = useAtom(termsApprovalStateAtom);
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [requestType, setRequestType] = useState<CiRequestTypes | null>(null);
  const { ciFormRef, prepareData, prepareCiAuthenticationData } = usePrepareCiAuthData(requestType ?? CI_REQUEST_TYPES.COLLECTION, returnUrl);
  const { sendClickEvent } = useUserEventLogger();
  const footerElement = document.getElementById('bznav-footer');

  const isActiveAuthButton = signRequiredStep.reAgreeTermKeys.length === 0 || termsContent.every((term) => termsApprovalState?.[term.key] || !term.isRequired);
  useCiHistoryController('entry');

  const getTerms = useCallback(async () => {
    if (!authService) return;
    const response = await authService.getTerms(signRequiredStep.reAgreeTermKeys);
    if (response.ok) {
      const termsData = await response.json();
      if (termsData.result) {
        setTermsApprovalState((prev) => {
          const updated = { ...prev };
          termsData.terms.forEach((term: Term) => {
            updated[term.key] = false;
          });
          return updated;
        });
        setTermsContent(termsData.terms);
      }
    }
  }, [authService, setTermsApprovalState, signRequiredStep.reAgreeTermKeys]);

  const startAuthentication = () => {
    sendClickEvent('auth-phone-request_next', { termKeys: signRequiredStep.reAgreeTermKeys, requestType, returnUrl });
    prepareCiAuthenticationData();
  };

  useEffect(() => {
    if (requestType) return;

    const searchParams = new URLSearchParams(window.location.search);
    setReturnUrl(searchParams.get('return_url'));
    setRequestType((searchParams.get('request_type') as CiRequestTypes) ?? CI_REQUEST_TYPES.COLLECTION);

    if (signRequiredStep.reAgreeTermKeys.length > 0 && termsContent.length === 0) {
      getTerms();
    }
  }, [getTerms, requestType, signRequiredStep.reAgreeTermKeys.length, termsContent.length]);

  return (
    <>
      <MainTitle
        graphic={<Svg width={60} height={60} src={'icg_shield_md.svg'} loader={<div className={'h-[60px]'} />} className={'-ml-1'} />}
        title={'안전한 서비스 이용을 위해\n휴대폰 본인인증을 진행해주세요'}
        description={'인증 정보는 본인 확인 목적 외에는 사용되지 않으며, 안전하게 보호됩니다.'}
      />
      {signRequiredStep.reAgreeTermKeys.length > 0 && (
        <>
          <Divider className={'my-normal-medium'} />
          {termsContent.map((term) => (
            <List
              key={term.key}
              leadingElement={
                <Checkbox
                  isBox
                  checked={termsApprovalState?.[term.key]}
                  onClick={(event) => {
                    event.stopPropagation();
                    sendClickEvent('auth-phone-request_terms', { termKey: term.key, termName: term.name, state: !termsApprovalState?.[term.key] });
                    setTermsApprovalState((prev) => ({ ...prev, [term.key]: !prev?.[term.key] }));
                  }}
                />
              }
              size={'xsmall'}
              title={`${term.isRequired ? '[필수]' : '[선택]'} ${term.name}`}
              primaryAction={true}
              onClick={() =>
                NiceModal.show(TermDrawer, {
                  link: term.popupUrl,
                  keepMounted: true
                })
              }
            />
          ))}
        </>
      )}
      <CIPrepareDataForm ciFormRef={ciFormRef} prepareData={prepareData} />
      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <div className={'flex flex-col gap-normal-small w-full'}>
              <AdditionalInfo
                info={'한 번만 인증하면, 이후에는 바로 이용할 수 있어요'}
                divider={false}
                className={'text-neutral-foreground-low text-center caption-medium-regular'}
              />
              <BoxButton variant="filled-primary" size="large" className="w-full" disabled={!isActiveAuthButton} onClick={startAuthentication}>
                {isActiveAuthButton ? '휴대폰 본인인증 하기' : '약관에 동의해주세요'}
              </BoxButton>
            </div>
          </StickyBottomWrapper>,
          footerElement
        )}
      {requestType && (
        <PageViewEventLogger pageName={'auth-phone-request'} eventProperty={{ termKeys: signRequiredStep.reAgreeTermKeys, requestType, returnUrl }} />
      )}
    </>
  );
};
